/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@100mslive/roomkit-react';
import { useDispatch } from 'react-redux';
import { GridCenterView } from '../components/gridView';
import CodeEditor from '../components/Pages/components/portal/CodeEditor';
import Question from '../components/Pages/components/portal/Question';
import QuestionInfo from '../components/Pages/components/portal/QuestionInfo';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { getQuestion } from '../redux/action';
import { DEFAULT_FORM } from '../common/constants';
import { defaultVoice } from '../utils/constants';
export const ConferenceMainView = ({
    form,
    questionInfo,
    handleEndCall,
    onlineStatus,
    setAnswerDuration,
    candidatePeer,
    displayInput,
    updateSocket,
    setCodeEditorForm,
    codeEditorForm,
    isQuestionTypeCoding,
    timer,
    changeInCode,
    setChangeInCode,
    setDisplayInput,
    saveCodingQuestion,
    setIsQuestionTypeCoding,
    codeOutput,
    setQuestionInfo,
    callEnd,
    microsoftKeys = [],
}) => {
    let currentAudio = null;
    const dispatch = useDispatch();
    const videoRef = useRef(null);
    const synthesizerRef = useRef(null);
    const [audioPlayer, setAudioPlayer] = useState();
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [questionEnded, setQuestionEnded] = useState(false);
    const [isQuestionRunning, setIsQuestionRunning] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [waitingAnswerTime, setWaitingAnswerTime] = useState(false);
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [answerTime, setAnswerTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [questionHide, setQuestionHide] = useState(true);

    const randomIndex = Math.floor(Math.random() * microsoftKeys.length);

    const logSuccess = (message) => console.log(`✅ Success: ${message}`);
    const logError = (message, error) => console.error(`❌ Error: ${message}`, error);

    useEffect(() => {
        const submitAndFetchQuestion = async () => {
            initializeSpeechServices();
            if (callEnd) {
                if (form?.codingEnabled) await handleSubmit();
                else await handleEndCall(false, 'Auto Interview Submitted');
            }
        };

        submitAndFetchQuestion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, form?.codingEnabled, callEnd]);

    useEffect(() => {
        if (displayInput?.message && displayInput?.audioPlay) {
            logSuccess('Speaking text from display input');
            speakText(displayInput.message);
        }
    }, [displayInput?.message]);

    useEffect(() => {
        if (!isPlaying && isQuestionRunning) {
            let answerTimer;
            if (!waitingAnswerTime) {
                setWaitingAnswerTime(true);
                logSuccess('Answer timer started');
            }
            const ansTimerFunc = () => {
                if (answerTime > 0) {
                    setAnswerTime(answerTime - 1);
                    updateSocket('answerTime', answerTime - 1);
                    setAnswerDuration((prev) => prev + 1);
                    logSuccess('Decrementing answer time');
                } else {
                    clearInterval(answerTimer);
                    setWaitingAnswerTime(false);
                    setQuestionEnded(true);
                    setIsSubmitModalOpen(false);
                    updateSocket('isSubmitModalOpen', false);
                    logSuccess('Answer timer ended');
                }
            };
            answerTimer = setInterval(async () => {
                !onlineStatus ? clearInterval(answerTimer) : ansTimerFunc();
            }, 1000);
            return () => {
                clearInterval(answerTimer);
            };
        }
    }, [answerTime, onlineStatus, isPlaying]);

    useEffect(() => {
        const handleSubmission = async () => {
            if (!isPlaying && questionEnded && !waitingAnswerTime && answerTime === 0) {
                logSuccess('Handling regular question submission');
                await handleNextQuestionClick();
            }
        };
        handleSubmission();
    }, [questionEnded, isPlaying, answerTime, waitingAnswerTime]);

    useEffect(() => {
        if (audioPlayer) {
            logSuccess('audioPlayer useEffect triggered');
            const interval = setInterval(async () => {
                try {
                    if (audioPlayer.paused) {
                        logSuccess('Audio player paused');
                        clearInterval(interval);
                        handleAudioError();
                        logSuccess('handleAudioEnd called from audioPlayer useEffect');
                        setAudioPlayer(null);
                    } else if (!isPlaying) {
                        setIsPlaying(true);
                        updateSocket('isPlaying', true);
                        logSuccess('Audio is playing');
                    }
                } catch (error) {
                    logError('Error in audioPlayer interval', error);
                    clearInterval(interval); // Cleanup in case of an error
                }
            }, 100);
            return () => clearInterval(interval); // Cleanup when audioPlayer changes
        }
    }, [audioPlayer, isPlaying]);

    const timerEnd = () => {
        setAnswerTime(0);
        updateSocket('answerTime', 0);
        logSuccess('Timer ended and reset state successfully.');
    };

    const initializeSpeechServices = () => {
        try {
            console.log('Initializing speech services.');
            // const subscriptionKey = process.env.REACT_APP_MICROSOFT_KEY;
            // const region = process.env.REACT_APP_MICROSOFT_REGION;
            const subscriptionKey = microsoftKeys[randomIndex]?.value;
            const region = microsoftKeys[randomIndex]?.additionalValue;

            if (!subscriptionKey || !region) {
                logError('Microsoft subscription key or region is missing from environment variables.');
                return;
            }

            logSuccess('Successfully retrieved Microsoft subscription key and region.');

            const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(subscriptionKey, region);
            speechConfig.speechRecognitionLanguage = 'en-IN';
            speechConfig.speechSynthesisVoiceName = defaultVoice;
            synthesizerRef.current = new SpeechSDK.SpeechSynthesizer(speechConfig, null);
        } catch (error) {
            logError(`Error initializing speech services: ${error}`);
        }
    };

    const generateAudioData = async (text) => {
        const synthesizer = synthesizerRef.current;
        return new Promise((resolve, reject) => {
            synthesizer.speakTextAsync(
                text,
                (result) => {
                    if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
                        logSuccess('Audio synthesis completed successfully');
                        resolve(result.audioData);
                    } else {
                        const errorDetails = `Speech synthesis canceled: ${result.errorDetails}`;
                        logError(errorDetails);
                        reject(new Error(errorDetails));
                    }
                },
                (error) => {
                    logError('Error during speech synthesis', error);
                    reject(error);
                }
            );
        });
    };

    const createAudioURL = (audioData) => {
        const blob = new Blob([audioData], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        logSuccess(`Audio URL created: ${url}`);
        return url;
    };

    const speakText = async (text) => {
        if (!text || !synthesizerRef.current) {
            logError('Invalid input or Speech synthesizer not initialized');
            return;
        }
        try {
            const audioData = await generateAudioData(text);
            if (audioData) {
                const value = { ...displayInput, audioData };
                setDisplayInput(value);
                updateSocket('displayInput', value);
                const url = createAudioURL(audioData);
                logSuccess(`Audio URL generated: ${url}`);
                playAudio(url);
            }
        } catch (error) {
            logError('Error in speakText', error);
        }
    };

    const stopCurrentAudio = () => {
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            currentAudio = null;
            logSuccess('Current audio stopped');
        }
    };

    const playAudio = async (url) => {
        logSuccess('playAudio called');
        stopCurrentAudio();
        if (!url) {
            logError('No audio URL provided');
            return;
        }
        const audio = new Audio(url);
        setAudioPlayer(audio);
        currentAudio = audio;
        setupAudioListeners(audio);
        try {
            setIsPlaying(true);
            updateSocket('isPlaying', true);
            await audio.play();
            setQuestionHide(false);
            logSuccess('Audio playback started');
        } catch (error) {
            console.error('Error playing audio:', error);
        }
    };

    const handleAudioError = (error) => {
        logSuccess('handleAudioError called');
        setIsPlaying(false);
        setWaitingAnswerTime(false);
        updateSocket('isPlaying', false);
        if (error) logError('Audio playback error', error);
    };

    const setupAudioListeners = (audio) => {
        audio.addEventListener('error', handleAudioError);
        const stopAudio = () => {
            try {
                if (audio) {
                    audio.pause();
                    audio.currentTime = 0;
                    setIsPlaying(false);
                    updateSocket('isPlaying', false);
                    logSuccess('Audio stopped from event listener');
                }
            } catch (error) {
                logError('Error in stopAudio listener', error);
            }
        };
        window.addEventListener('beforeunload', stopAudio);
        window.addEventListener('popstate', stopAudio);
        logSuccess('Audio listeners set up successfully');
    };

    const handleNextQuestionClick = async () => {
        if (!isButtonDisabled) {
            setIsQuestionRunning(false);
            setIsButtonDisabled(true);
            timerEnd();
            if (isQuestionTypeCoding) {
                logSuccess('Saving coding question data');
                saveCodingQuestion({ data: codeOutput });
            }
            try {
                logSuccess('Handling next question submission');
                const finalContent = `That's all from my side ${form?.candidateName}.\nThank you for your time and for sharing your experiences and insights with us.\nGood luck! Your interview has been completed.\nPlease click on the SUBMIT button to end the interview.`;

                const value = {
                    message: finalContent,
                    displayText: finalContent,
                    audioPlay: true,
                    audioData: null,
                    questionType: 'coding',
                    isDisabled: false,
                };

                setIsQuestionTypeCoding(false);
                setDisplayInput(value);
                updateSocket('displayInput', value);

                // Delay calling handleEndCall by 30 seconds
                setTimeout(async () => {
                    await handleEndCall(false, 'Auto Interview Submitted');
                }, 30000);
            } catch (error) {
                console.error('Error in handleNextQuestionClick:', error);
            } finally {
                setIsButtonDisabled(false);
                setIsSubmitModalOpen(false);
                updateSocket('isSubmitModalOpen', false);
            }
        }
    };

    const handleSubmit = async () => {
        try {
            logSuccess('Handle submit process started');
            if (onlineStatus) {
                dispatch(
                    getQuestion({
                        data: {
                            interviewId: form?.interviewId,
                            questionNumber: 1,
                        },
                        callback: (data) => {
                            logSuccess(`Question data fetched successfully: ${JSON.stringify(data)}`);
                            setQuestionInfo((prevState) => ({
                                ...prevState,
                                question: data?.questionText,
                                questionAnswer: data?.questionAnswer || '',
                                prepareTime: data?.timeToPrepare,
                                timeToAnswer: data?.timeToAnswer,
                                questionNumber: 1,
                                actualQuestion: data?.question,
                                questionType: 'coding',
                            }));
                            logSuccess('Handling coding question type');
                            const codingForm = {
                                ...DEFAULT_FORM,
                                preBuiltFunction: data?.preBuildFunction,
                                difficultyLevel: data?.difficultyLevel,
                                questionId: data?.questionId,
                                neededLanguage: data?.programmingLanguage,
                            };
                            setCodeEditorForm(codingForm);
                            updateSocket('codeEditorForm', codingForm);
                            setIsQuestionTypeCoding(true);
                            setIsQuestionRunning(true);
                            logSuccess('Setting up next question');
                            updateSocket('prepareTime', data?.timeToPrepare);
                            setAnswerTime(data?.timeToAnswer);
                            updateSocket('answerTime', data?.timeToAnswer);
                            const value = {
                                message: data?.questionText,
                                displayText: data?.question ?? data?.questionText,
                                audioPlay: true,
                                audioData: null,
                                questionType: 'coding',
                                isDisabled: false,
                            };
                            setDisplayInput(value);
                            updateSocket('displayInput', value);
                        },
                    })
                );
            } else {
                logError('Cannot submit question: offline status');
            }
        } catch (error) {
            logError('Error in handleSubmit', error);
        }
    };

    return (
        <Box
            css={{
                w: '100%',
                flex: '1 1 0',
                minHeight: 0,
                paddingBottom: 'env(safe-area-inset-bottom)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
            id="conferencing"
            data-testid="conferencing"
        >
            <QuestionInfo form={form} timer={timer} handleEndCall={handleEndCall} />
            <Flex
                css={{
                    width: '100%',
                    overflow: 'hidden',
                    height: 'auto',
                    flexGrow: '1',
                }}
                direction={{ '@initial': 'row', '@md': 'column' }}
                ref={videoRef}
                className="video_question_container"
            >
                <Flex
                    css={{
                        size: '100%',
                        flexDirection: 'column',
                        position: 'relative',
                    }}
                    className="video_container"
                    direction={{
                        '@initial': 'column',
                        '@md': 'column',
                    }}
                >
                    {isQuestionTypeCoding ? (
                        <CodeEditor
                            updateSocket={updateSocket}
                            form={codeEditorForm}
                            setForm={setCodeEditorForm}
                            handleSubmit={handleNextQuestionClick}
                            changeInCode={changeInCode}
                            setChangeInCode={setChangeInCode}
                            setIsSubmitModalOpen={setIsSubmitModalOpen}
                            isSubmitModalOpen={isSubmitModalOpen}
                            isPlaying={isPlaying}
                            question={questionInfo?.actualQuestion}
                            lastQuestion={true}
                        />
                    ) : (
                        <GridCenterView peers={candidatePeer} maxTileCount={1} />
                    )}
                </Flex>
                <Question
                    questionInfo={questionInfo}
                    isSpeaking={isPlaying}
                    answerTime={answerTime}
                    handleEndCall={handleEndCall}
                    candidatePeer={candidatePeer}
                    isButtonDisabled={isButtonDisabled}
                    handleNextQuestionClick={handleNextQuestionClick}
                    isNextButtonEnabled={answerTime < questionInfo?.timeToAnswer && answerTime > 0}
                    question={
                        isPlaying || questionHide
                            ? displayInput?.message
                            : isQuestionTypeCoding && answerTime > 0
                              ? displayInput?.displayText
                              : ''
                    }
                    position={position}
                    setPosition={setPosition}
                    isQuestionRunning={isQuestionRunning}
                />
            </Flex>
        </Box>
    );
};
